import { useMemo, useState } from "react";
import { Snackbar } from "@mui/material";
import CustomDataTable from "@components/DataTable/CustomDataTable";
import type { Header } from "@components/DataTable/CustomDataTable.types.ts";
import type {
  FilterFieldDefinition,
  FilterItem,
} from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import type {
  BerthVisitedCode,
  DeficiencyCompany,
  DeficiencySearchResponse,
} from "@services/deficiencies/types";
import { getBerthVisitedCodes, getCompanies, searchDeficiencies } from "@services/deficienciesApi";
import { useQuery } from "@tanstack/react-query";
import { convertUtcToUae, formatDateToDDMMYYYY } from "@utils/date-time.tsx";
import { checkPermission } from "@utils/user-permissions.tsx";
import { useTranslation } from "react-i18next";
import LoadingOverlay from "../../../components/LoadingOverlay/LoadingOverlay";
import { FOTT_BERTH_FROM_TO_LOCATION } from "../create/constants";
import ResolveDeficiencyDrawer from "../deficiencies-details/flow/ResolveDeficiencyDrawer";
import { canResolveDeficiency } from "../deficiencies-details/flow/utils";
import { useDeficiencyFlow } from "../hooks/useDeficiencyFlow";
import {
  buildDeficienciesFilterBody,
  buildDeficienciesFilterFields,
  DEFICIENCIES_FIELDS,
  formatBerthVisited,
  formatDeficiencyStatus,
  formatLoa,
  getDeficiencyBadgeType,
  truncateText,
} from "./utils.tsx";

const LANG_BASE = "deficiencies";
const TEST_ID_BASE = "deficiencies";

const LOCAL_STORAGE_PAGE_KEY = "deficiencies-page";
const LOCAL_STORAGE_PAGE_SIZE_KEY = "deficiencies-page-size";
const LOCAL_STORAGE_FILTERS_KEY = "deficiencies-filters";
const EMPTY_ROLES: string[] = [];
export const Deficiencies = () => {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const userRoles = user?.roles ?? EMPTY_ROLES;
  const userActionPermissions = useAuthStore((state) => state.userActionPermissions ?? EMPTY_ROLES);

  const canRequestFottDeficiency = checkPermission(
    userActionPermissions,
    "canRequestFottDeficiency",
  );
  const {
    openDrawers,
    selectedDeficiencyId,
    openDrawer,
    closeDrawer,
    handleResolveSubmit,
    isResolvingDeficiency,
  } = useDeficiencyFlow();
  const [filters, setFilters] = useState<FilterItem[]>(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_FILTERS_KEY);
    return saved ? JSON.parse(saved) : [];
  });

  const [page, setPage] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PAGE_KEY);
    return saved ? parseInt(saved, 10) : 1;
  });

  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PAGE_SIZE_KEY);
    return saved ? parseInt(saved, 10) : 10;
  });

  const { data, isLoading, error } = useQuery<DeficiencySearchResponse>({
    queryKey: ["deficiencies", page, pageSize, filters],
    queryFn: () =>
      searchDeficiencies({
        page,
        pageSize,
        body: buildDeficienciesFilterBody(filters),
      }),
    staleTime: 0,
    refetchOnMount: true,
  });

  const { data: companies = [] } = useQuery<DeficiencyCompany[]>({
    queryKey: ["deficiency-companies"],
    queryFn: getCompanies,
  });

  const { data: berthVisitedCodes = [] } = useQuery<BerthVisitedCode[]>({
    queryKey: ["deficiency-berth-visited-codes", FOTT_BERTH_FROM_TO_LOCATION],
    queryFn: () => getBerthVisitedCodes(FOTT_BERTH_FROM_TO_LOCATION),
  });

  const headers: Header[] = useMemo(() => {
    return Object.values(DEFICIENCIES_FIELDS).map((field) => ({
      field,
      columnName:
        field === "refOrderNumber"
          ? t(`${LANG_BASE}.headerLabels.id`)
          : t(`${LANG_BASE}.headerLabels.${field}`),
    }));
  }, [t]);

  const filterFields: Record<string, FilterFieldDefinition> = useMemo(
    () => buildDeficienciesFilterFields(t, companies, berthVisitedCodes),
    [berthVisitedCodes, companies, t],
  );

  const onApplyFilters = (newFilters: FilterItem[]) => {
    setFilters(newFilters);
    localStorage.setItem(LOCAL_STORAGE_FILTERS_KEY, JSON.stringify(newFilters));
    setPage(1);
    localStorage.setItem(LOCAL_STORAGE_PAGE_KEY, "1");
  };

  const onClearFilters = () => {
    setFilters([]);
    localStorage.removeItem(LOCAL_STORAGE_FILTERS_KEY);
  };

  const rows = (data?.items ?? []).map((item) => {
    return {
      id: item.deficiencyId,
      deficiencyId: item.deficiencyId,
      refOrderNumber: item.refOrderNumber,
      imoNumber: String(item.imoNumber),
      vesselName: item.vesselName,
      shippingAgency: item.shippingAgency,
      tripNoPlda: item.tripNoPlda,
      berthVisited: formatBerthVisited(item.berthVisited),
      loa: formatLoa(item.loa),
      deficiencyDetails: truncateText(item.details),
      deficiencyDateTime: item.createdAt
        ? formatDateToDDMMYYYY(convertUtcToUae(item.createdAt), true)
        : "-",
      submittedBy: item.createdBy,
      resolutionCreatedAt: item.resolutionCreatedAt
        ? formatDateToDDMMYYYY(convertUtcToUae(item.resolutionCreatedAt), true)
        : "-",

      approvedRejectedAt: item.approvedRejectedAt
        ? formatDateToDDMMYYYY(convertUtcToUae(item.approvedRejectedAt), true)
        : "-",
      approvedRejectedBy: item.approvedRejectedBy ?? "-",
      deficiencyStatus: item.status,
      status: formatDeficiencyStatus(item.status),
      badgeType: getDeficiencyBadgeType(item.status),
    };
  });

  const isShippingAgent = userRoles.includes("shipping-agent");

  if (error) {
    return <Snackbar open autoHideDuration={6000} message={(error as Error).message} />;
  }

  if (isLoading) {
    return <LoadingOverlay loading={isLoading} />;
  }

  return (
    <div data-testid={TEST_ID_BASE}>
      <CustomDataTable
        testId={TEST_ID_BASE}
        actionName={canRequestFottDeficiency ? t(`${LANG_BASE}.add-deficiency`) : ""}
        headers={headers}
        rows={rows}
        page={page}
        title={t(`${LANG_BASE}.grid-title`)}
        pageSize={pageSize}
        totalCount={data?.totalElements ?? 0}
        canPreview={true}
        onPageChange={(newPage, newSize) => {
          setPage(newPage);
          setPageSize(newSize);
          localStorage.setItem(LOCAL_STORAGE_PAGE_KEY, String(newPage));
          localStorage.setItem(LOCAL_STORAGE_PAGE_SIZE_KEY, String(newSize));
        }}
        actionColumn={
          isShippingAgent
            ? {
                actionTitle: t("deficiencies.actions.resolve"),
                isActionEnabled: (row) => canResolveDeficiency(row.deficiencyStatus, userRoles),
                onActionClick: (row) => {
                  if (!canResolveDeficiency(row.deficiencyStatus, userRoles)) {
                    return;
                  }

                  openDrawer("resolveDrawer", row.id);
                },
              }
            : undefined
        }
        navigationLinkBase="deficiency-details"
        navigationParam="deficiencyId"
        pageSizeOptions={[5, 10, 15, 20, 25]}
        filters={filters}
        onApplyFilters={onApplyFilters}
        onClearFilters={onClearFilters}
        availableFields={filterFields}
      />
      <ResolveDeficiencyDrawer
        isOpen={openDrawers.resolveDrawer}
        onClose={closeDrawer}
        deficiencyId={selectedDeficiencyId}
        onSubmit={handleResolveSubmit}
        isSubmitting={isResolvingDeficiency}
      />
    </div>
  );
};

export default Deficiencies;
